import * as React from 'react';
import { useWatch } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { useFormContext } from './Form';
import { useFieldError } from './utils';

export interface CheckboxFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'id' | 'type'
> {
  name: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  id?: string;
  /** When true, renders as a pill with green highlight when checked. */
  pill?: boolean;
}

export function CheckboxField({
  name,
  label,
  description,
  id,
  className,
  pill,
  ...inputProps
}: CheckboxFieldProps) {
  const { register, control } = useFormContext();
  const error = useFieldError(name);
  const fieldId = id ?? `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;
  const descId = description ? `${fieldId}-desc` : undefined;
  const checked = useWatch({ control, name });

  const labelClass = pill
    ? 'inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 transition-colors cursor-pointer ' +
      (checked
        ? 'border-green-600 bg-green-600/15 text-green-700 dark:border-green-500 dark:bg-green-500/15 dark:text-green-400'
        : 'border-border bg-muted/30 text-muted-foreground')
    : 'flex items-center gap-2';

  return (
    <div>
      <Label htmlFor={fieldId} className={labelClass}>
        <input
          id={fieldId}
          type="checkbox"
          className="h-4 w-4 rounded border-border accent-green-600"
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error ? errorId : undefined, descId].filter(Boolean).join(' ') || undefined
          }
          {...register(name)}
          {...inputProps}
        />
        <span className="text-sm">{label}</span>
      </Label>
      {description && (
        <p id={descId} className="mt-1 text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
